import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import httpInstances from '@/api/axiosInstance';
import {
  isRestrictedAccessReason,
  normalizeForbiddenReason,
  SUPPRESSION_LIST_REASON
} from '@/features/AuthAccess/model/accessRestrictions';
import { analytics } from '@/services/analytics';
import { clearStoragesFromTokensAndUserInfo } from '@/services/storage';
import type { ApiErrorPayload, ErrorResponse } from '@/types/errors';
import { getInternalBasePath } from '@/utils/environmentPaths';
import { extractErrorMessage } from '@/utils/extractErrorMessage';
import { getForbiddenReason } from '@/utils/getForbiddenReason';
import { handleThunkErrorWithStatus } from '@/utils/thunkErrorHandler';

export type AuthApiState = {
  status: 'idle' | 'loading' | 'failed' | 'fulfilled' | 'notFound' | 'onSuppressionList';
  error: string | null;
  forbiddenReason: string | null;
};

type LoginApiErrorResponse = {
  errors?: string[];
};

const initialState: AuthApiState = {
  status: 'idle',
  error: null,
  forbiddenReason: null
};

export const loginGX = createAsyncThunk(
  'loginGX',
  async (request: { email: string; captchaToken: string }, { rejectWithValue }) => {
    try {
      await httpInstances.axiosInternalInstance.post(
        `${getInternalBasePath()}login`,
        { email: request.email },
        { headers: { captchaToken: request.captchaToken } }
      );
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        return rejectWithValue({
          data: error.response.data,
          status: error.response.status,
          forbiddenReason: normalizeForbiddenReason(getForbiddenReason(error.response.headers))
        }) as never;
      }

      try {
        return handleThunkErrorWithStatus(error, rejectWithValue) as never;
      } catch (e) {
        analytics.captureLoginError(error);
        throw e;
      }
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    resetStatus(state) {
      state.status = 'idle';
      state.forbiddenReason = null;
    },
    logout() {
      clearStoragesFromTokensAndUserInfo();
      analytics.reset();
      return initialState;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(loginGX.pending, state => {
        state.status = 'loading';
        state.error = null;
        state.forbiddenReason = null;
      })
      .addCase(loginGX.fulfilled, state => {
        state.status = 'fulfilled';
        state.forbiddenReason = null;
      })
      .addCase(loginGX.rejected, (state, action) => {
        const response = action.payload as ApiErrorPayload | undefined;
        const forbiddenReason = response?.forbiddenReason ?? null;

        state.forbiddenReason = null;

        if (response?.status === 404) {
          state.status = 'notFound';
        } else if (
          response?.status === 403 &&
          (forbiddenReason === SUPPRESSION_LIST_REASON ||
            (response.data as LoginApiErrorResponse).errors?.some(e =>
              e.includes('is on suppression list')
            ))
        ) {
          state.status = 'onSuppressionList';
        } else if (response?.status === 403 && isRestrictedAccessReason(forbiddenReason)) {
          analytics.captureLoginRejection(action.payload);
          state.status = 'failed';
          state.forbiddenReason = forbiddenReason;
          const data = response?.data as ErrorResponse | undefined;
          state.error = extractErrorMessage(
            data?.message ? data : null,
            action.error.message,
            'Login failed'
          );
        } else {
          analytics.captureLoginRejection(action.payload);
          state.status = 'failed';
          const data = response?.data as ErrorResponse | undefined;
          state.error = extractErrorMessage(
            data?.message ? data : null,
            action.error.message,
            'Login failed'
          );
        }
      });
  }
});

export const { resetStatus, logout } = authSlice.actions;

export default authSlice.reducer;
